import { lazy, memo, Suspense, useCallback } from "react";
import { IAnnouncement, InfoBarBackgroundThemeEnum } from "@/common/api/services/types";
import { clsx } from "clsx";
import CloseIcon from "@/common/images/icons/x.svg?react";
import "./InfoBar.scss";

const BlackFridayTheme = lazy(() => import("@/components/alert-theme-black-friday/BlackFridayTheme"));
const HalloweenTheme = lazy(() => import("@/components/alert-theme-halloween/HalloweenTheme"));
const ChristmasTheme = lazy(() => import("@/components/alert-theme-christmas/ChristmasTheme"));
const ValentineTheme = lazy(() => import("@/components/alert-theme-valentine/ValentineTheme"));
const StandardTheme = lazy(() => import("@/components/alert-theme-standard/StandardTheme"));

interface IInfoBar {
  onLinkAction: (id: string) => void;
  onCloseInfoBar: (id: string) => void;
  notification: IAnnouncement;
  showNotify: boolean;
  preventNavigation?: boolean;
}

export const InfoBar: React.FC<IInfoBar> = memo(({
  onLinkAction,
  onCloseInfoBar,
  notification,
  showNotify,
  preventNavigation,
}) => {
  const { background_theme, text, text_theme, cta_text, cta_link, close_button_enabled, id } = notification;

  const onLinkClick = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    await onLinkAction(id);

    if (!preventNavigation) {
      location.href = cta_link;
    }
  }, [cta_link, id, onLinkAction, preventNavigation]);

  const onClose = useCallback(() => {
    onCloseInfoBar(id);
  }, [id, onCloseInfoBar]);

  const renderDecor = useCallback(() => {
    switch (background_theme) {
      case InfoBarBackgroundThemeEnum.halloween:
        return <HalloweenTheme />;
      case InfoBarBackgroundThemeEnum.blackFriday:
        return <BlackFridayTheme />;
      case InfoBarBackgroundThemeEnum.christmas:
        return <ChristmasTheme />;
      case InfoBarBackgroundThemeEnum.valentine:
        return <ValentineTheme />;
      case InfoBarBackgroundThemeEnum.standard:
        return <StandardTheme />;
      default:
        return null;
    }
  }, [background_theme]);

  return (
    <div className={clsx(
      "AnnouncementBar",
      !showNotify && "AnnouncementBar_isHidden",
      `AnnouncementBar_is${background_theme}`,
      `AnnouncementBar_is${text_theme}`,
      close_button_enabled && "AnnouncementBar_withCloseBtn"
    )}>
      <div className="AnnouncementBar-Wrap">

        <div className="AnnouncementBar-Info">
          <p className="AnnouncementBar-Text">{text}</p>
          <button className="AnnouncementBar-Link" rel="info-bar" onClick={onLinkClick}>{cta_text}</button>
        </div>

        {!!close_button_enabled &&
          <button className="AnnouncementBar-CloseBtn" onClick={onClose} aria-label="Close Information Bar">
            <CloseIcon />
          </button>
        }

        <Suspense>
          {renderDecor()}
        </Suspense>
      </div>
    </div>
  );
});
